import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Rocket, Check } from 'lucide-react';

const content = {
  de: {
    badge: 'NEU',
    headline: 'WEBSITES & WEBAPPS IN 3 TAGEN ONLINE',
    sub: 'Professionelle Webseiten und WebApps zum transparenten Festpreis – klar strukturiert, mobil optimiert und innerhalb kürzester Zeit online.',
    packages: [
      { name: 'Launch Starter', price: '399,- €', desc: 'Webseite mit bis zu 5 Sektionen' },
      { name: 'Business Connect', price: '699,- €', desc: 'Inkl. Adminbereich & Kontaktmanagement', featured: true },
      { name: 'Custom WebApp Pro', price: 'Auf Anfrage', desc: 'Individuelle WebApps & Portale' }
    ],
    cta: 'MEHR ERFAHREN',
    close: 'Schließen'
  },
  it: {
    badge: 'NUOVO',
    headline: 'SITI WEB & WEBAPP ONLINE IN 3 GIORNI',
    sub: 'Siti web e webapp professionali a prezzo fisso trasparente – strutturati, ottimizzati per mobile e online in brevissimo tempo.',
    packages: [
      { name: 'Launch Starter', price: '399,- €', desc: 'Sito web con fino a 5 sezioni' },
      { name: 'Business Connect', price: '699,- €', desc: 'Incl. area admin & gestione contatti', featured: true },
      { name: 'Custom WebApp Pro', price: 'Su richiesta', desc: 'WebApp individuali & portali' }
    ],
    cta: 'SCOPRI DI PIÙ',
    close: 'Chiudi'
  }
};

export default function ProductPopup() {
  const { lang } = useLanguage();
  const t = content[lang];
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const seen = sessionStorage.getItem('enbi_product_popup_seen');
    if (!seen) {
      const timer = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setVisible(false);
    sessionStorage.setItem('enbi_product_popup_seen', 'true');
  };

  const scrollToProduct = () => {
    handleClose();
    setTimeout(() => {
      document.getElementById('product')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4 md:p-8"
          onClick={handleClose}
          data-testid="product-popup-overlay"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="relative bg-enbi-navy w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="product-popup"
          >
            {/* Gold top bar */}
            <div className="h-1 bg-enbi-gold" />

            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors z-10"
              data-testid="product-popup-close"
            >
              <X size={20} strokeWidth={1.5} />
            </button>

            <div className="p-8 sm:p-10">
              {/* Badge */}
              <div className="flex items-center gap-3 mb-5">
                <span className="bg-enbi-gold text-enbi-navy text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1">
                  {t.badge}
                </span>
                <Rocket className="text-enbi-gold" size={18} strokeWidth={1.5} />
              </div>

              <h2 className="font-outfit text-xl sm:text-2xl font-bold text-white uppercase tracking-tight mb-4">
                {t.headline}
              </h2>
              <p className="text-white/50 text-sm leading-relaxed mb-8">
                {t.sub}
              </p>

              {/* Mini package overview */}
              <div className="space-y-3 mb-8">
                {t.packages.map((pkg, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between p-3.5 border ${
                      pkg.featured ? 'border-enbi-gold/50 bg-enbi-gold/[0.08]' : 'border-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Check className="text-enbi-gold" size={14} strokeWidth={2} />
                      <div>
                        <span className={`text-sm font-semibold ${pkg.featured ? 'text-enbi-gold' : 'text-white'}`}>{pkg.name}</span>
                        <p className="text-white/40 text-xs">{pkg.desc}</p>
                      </div>
                    </div>
                    <span className={`text-sm font-bold ${pkg.featured ? 'text-enbi-gold' : 'text-white/70'}`}>{pkg.price}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={scrollToProduct}
                data-testid="product-popup-cta"
                className="w-full bg-enbi-gold text-enbi-navy py-4 text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-enbi-gold-light transition-all duration-300"
              >
                {t.cta}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
